'use client';

import classNames from 'classnames';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './page-progress-loader.module.scss';

const NAVIGATION_START_EVENT = 'navigation-start';

const FINISH_DELAY = 1000; // animation finish
const FAILSAFE_TIMEOUT = 5000; // hard stop

const PageProgressLoader = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const prevUrlRef = useRef<string | null>(null);
  const isNavigatingRef = useRef(false);

  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const failsafeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isVisible, setIsVisible] = useState(false);
  const [isDone, setIsDone] = useState(false);

  const startNavigation = useCallback(() => {
    isNavigatingRef.current = true;
    setIsVisible(true);
    setIsDone(false);

    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    if (failsafeTimeoutRef.current) {
      clearTimeout(failsafeTimeoutRef.current);
    }

    failsafeTimeoutRef.current = setTimeout(() => {
      isNavigatingRef.current = false;
      setIsDone(true);

      hideTimeoutRef.current = setTimeout(() => {
        setIsDone(false);
        setIsVisible(false);
      }, FINISH_DELAY);
    }, FAILSAFE_TIMEOUT);
  }, []);

  // Start
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement | null)?.closest('a');

      if (!anchor) return;

      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const href = anchor.getAttribute('href');

      if (!href) return;

      const current = new URL(window.location.href);
      const target = new URL(href, current);

      // Ignore hash-only navigation
      if (
        target.pathname === current.pathname &&
        target.search === current.search &&
        target.hash
      ) {
        return;
      }

      startNavigation();
    };

    const onProgrammaticNavigationStart = () => {
      startNavigation();
    };

    document.addEventListener('click', onClick);
    window.addEventListener(
      NAVIGATION_START_EVENT,
      onProgrammaticNavigationStart,
    );

    return () => {
      document.removeEventListener('click', onClick);
      window.removeEventListener(
        NAVIGATION_START_EVENT,
        onProgrammaticNavigationStart,
      );
    };
  }, [startNavigation]);

  // Finish
  useEffect(() => {
    const currentUrl = `${pathname}?${searchParams?.toString()}`;

    if (prevUrlRef.current === null) {
      prevUrlRef.current = currentUrl;

      return;
    }

    if (!isNavigatingRef.current) return;

    if (prevUrlRef.current === currentUrl) return;

    isNavigatingRef.current = false;
    prevUrlRef.current = currentUrl;

    // Clear failsafe
    if (failsafeTimeoutRef.current) {
      clearTimeout(failsafeTimeoutRef.current);
    }

    setIsDone(true);

    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    hideTimeoutRef.current = setTimeout(() => {
      setIsDone(false);
      setIsVisible(false);
    }, FINISH_DELAY);
  }, [pathname, searchParams]);

  return (
    <div
      aria-hidden="true"
      className={classNames(styles.component, {
        [styles.isVisible]: isVisible,
        [styles.isDone]: isDone,
      })}
    />
  );
};

export default PageProgressLoader;
